import {
    IGeoComplyResponse,
    IGeoLocationStrategy,
    IGeoLocationStrategyRunParams
} from "../geo-location.strategy.interface";
import {IServiceFactoryExtended} from "../../../service-factory-extended.interface";
import {DisposableObject} from "../../../../../utils/disposable-object";
import {GeoComplyPcClient} from "./geo-comply-pc-client";
import {isGeoComplyPluginInstallationRequired} from "../geo-comply-error-codes";
import {
    GeoInstallPluginPopupComponent
} from "../../../../components/geo/geo-install-plugin/geo-install-plugin-popup-component";
import {
    PRIORITY_LEVEL,
    PRIORITY_LEVEL_ZINDEX_MAPPER
} from "../../../../../sdk/src/services/overlay_manager/overlay_manager_levels";
import React from "react";
import {Check} from "@sparkware/app-utils";
import {GeoAnalyticsUserAction} from "../../geo-location-analytics";


export class GeoLocationPcStrategy extends DisposableObject implements IGeoLocationStrategy {
    constructor(private readonly services: IServiceFactoryExtended) {
        super();
        this._geoComplyClient = new GeoComplyPcClient(services);
    }

    private readonly _geoComplyClient: GeoComplyPcClient;

    dispose() {
        super.dispose();
        this._geoComplyClient.disconnect();
    }


    async runGeoLocation(params: IGeoLocationStrategyRunParams): Promise<IGeoComplyResponse> {
        let result = await this._geoComplyClient.runGeoLocation(params);

        if(isGeoComplyPluginInstallationRequired(result.failure?.geoComplyError?.errorCode)) {
            await this._downloadPlugIn(params);
            //After download dialog is closed we run again the geolocation.
            //If the dialog was canceled by user then we will get here again a missing plugin error, but we return this to the caller
            //and the caller will decide what to do next (like retry). We don't retry here because in different flows (login/open game) we might want to do things differently.
            //If the plugin was installed then we will run geo and if it will be successful then ok
            //otherwise again we return the error to the caller and the caller will decide what to do next.
            return await this._geoComplyClient.runGeoLocation(params);
        }

        return result;

    }

    private _getOSName(): string {
        if(this.services.device.os.isMacOS) {
            return 'mac';
        } else {
            return  'win'
        }
    }

    private _downloadPlugIn(params: IGeoLocationStrategyRunParams): Promise<void> {
        const config = this.services.configuration.servicesConfigs.GeoLocation.PC;
        const downloadUrl = `${config.installPath}?id=${config.GCinstallerID}&os=${this._getOSName()}&version=${config.downloadVersion}`;

        return new Promise<void>(resolve => {

            let pluginInstallationCheckInterval: any = null;

            params.analytics.reportUserAction(GeoAnalyticsUserAction.ShowDownloadPlugin);

            const dialogHandler = this.services.dialog.showModal(dialogHandler =>
                    <GeoInstallPluginPopupComponent downloadUrl={downloadUrl} analytics={params.analytics} />, {
                    title: this.services.translation.translate("GEO_PLUGIN_INSTALLATION_MSG_TITLE"),
                    icon: this.services.theme.currentTheme.icons.locationIcon,
                    forceTransparentBackground: true,
                    disableFullscreenMobile: true,
                    forceCentralization: true,
                    mobileCenterHeader: true,
                    isHighPriority: true,
                    forceZOrder: PRIORITY_LEVEL_ZINDEX_MAPPER[PRIORITY_LEVEL.TOUCH_ID] - 1,
                    onClosedByXButton: () => {
                      params.analytics.reportUserAction(GeoAnalyticsUserAction.CancelDownloadPlugin);
                    },
                    onClosed: () => {
                        if(pluginInstallationCheckInterval) {
                            clearInterval(pluginInstallationCheckInterval);
                        }

                        params.progressIndicator?.display();

                        resolve();
                    },
                    shouldBeClosedOnlyByUser: true,
                }
            );

            params.progressIndicator?.hide();


            //waiting for the plugin to be installed.
            pluginInstallationCheckInterval = setInterval(async () => {
                const connectResult = await this._geoComplyClient.connect();

                //if there is no error from connect it means the plugin was successfully installed and GeoComply connect was successful
                if(Check.isNullOrUndefined(connectResult)

                    // If we get an error other than plugin installation required it means that the plugin is installed
                    // but there are other issues with connect that will be handled by the upper layers (for example retry)
                    // So the only thing we can do here is to close the dialog
                    || !isGeoComplyPluginInstallationRequired(connectResult?.geoComplyError?.errorCode)) {
                    dialogHandler.close();
                }
            }, 500);

        })

    }

}